import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Store, Trash2, RefreshCw, CheckCircle, ExternalLink, ArrowUpRight, Key, Link2, HelpCircle, ChevronLeft, ChevronRight, Copy, Check, AlertTriangle } from "lucide-react";

const API = "/api";


function StoreNameEditor({ store, token, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(store.store_name || "");
  const [saving, setSaving] = useState(false);

  const save = async (event) => {
    event.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/stores/${store.id}/update`,
        { store_name: name.trim() }, { params: { token } });
      toast.success("Mağaza adı ve sipariş kayıtları güncellendi");
      setOpen(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mağaza adı güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (saving) return;
      if (value) setName(store.store_name || "");
      setOpen(value);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="mt-2" data-testid={`edit-store-name-${store.id}`}>
          Adı Düzenle
        </Button>
      </DialogTrigger>
      <DialogContent aria-describedby={`store-name-help-${store.id}`}>
        <DialogHeader><DialogTitle>Mağaza Adını Düzenle</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`store-name-${store.id}`}>Mağaza Adı</Label>
            <Input id={`store-name-${store.id}`} value={name} onChange={(event) => setName(event.target.value)} disabled={saving} required autoFocus />
            <p id={`store-name-help-${store.id}`} className="text-sm text-slate-500">
              Bu ad sipariş aktarımında kullanılır. Mevcut sipariş kayıtlarının mağaza adı da güncellenir.
              Shopify bağlantı adresi değişmez. Daha önce Nebim'e gönderilen siparişler yeniden gönderilmez.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
// ==================== LOCATION SELECTOR COMPONENT ====================
function LocationSelector({ storeId, currentLocationId, token, onUpdate }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stores/${storeId}/locations?token=${token}`);
      if (res.data?.success) {
        setLocations(res.data.locations);
      } else {
        toast.error("Location alınamadı: " + (res.data?.error || "Bilinmeyen hata"));
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Location hatası (${status}): ${detail}`);
      console.error("Location fetch error:", e.response?.data || e);
    }
    setLoading(false);
  };

  const selectLocation = async (locId) => {
    try {
      await axios.post(`${API}/stores/${storeId}/update?token=${token}`, { location_id: locId });
      toast.success(locId ? "Location seçildi" : "Tüm location'lar seçildi");
      onUpdate();
      setOpen(false);
    } catch (e) {
      toast.error("Location güncellenemedi");
    }
  };

  const currentName = locations.find(l => l.id === currentLocationId)?.name;

  return (
    <div className="relative">
      <Button 
        variant="outline" 
        size="sm" 
        className="h-7 text-xs"
        onClick={() => { setOpen(!open); if (!open) fetchLocations(); }}
        data-testid={`btn-location-${storeId}`}
      >
        {loading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
        {currentLocationId ? (currentName || `ID: ${currentLocationId}`) : "Tüm Location'lar"}
      </Button>
      {open && (
        <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 min-w-[220px]">
          <button
            className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-slate-100 ${!currentLocationId ? 'bg-blue-50 font-medium' : ''}`}
            onClick={() => selectLocation("")}
          >
            Tüm Location'lar
          </button>
          {locations.map(loc => (
            <button
              key={loc.id}
              className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-slate-100 ${currentLocationId === loc.id ? 'bg-blue-50 font-medium' : ''}`}
              onClick={() => selectLocation(loc.id)}
            >
              {loc.name} <span className="text-slate-400">({loc.id})</span>
            </button>
          ))}
          {locations.length === 0 && !loading && <p className="text-xs text-slate-400 px-3 py-1">Location bulunamadı</p>}
        </div>
      )}
    </div>
  );
}

// ==================== SETUP GUIDE COMPONENT ====================
function SetupGuide({ method, onClose }) {
  const [step, setStep] = useState(0);
  const [copied, setCopied] = useState(null);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const CopyButton = ({ text, id }) => (
    <button
      onClick={() => copyText(text, id)}
      className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-slate-100 hover:bg-slate-200 rounded text-xs font-mono transition-colors"
      data-testid={`copy-${id}`}
    >
      {copied === id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {copied === id ? "Kopyalandı" : "Kopyala"}
    </button>
  );

  const classicSteps = [
    {
      title: "Shopify Admin'e Giriş",
      content: (
        <div className="space-y-3">
          <p>Mağazanızın Shopify Admin paneline giriş yapın:</p>
          <div className="p-3 bg-slate-50 rounded-lg border text-sm">
            <code>https://MAGAZAADI.myshopify.com/admin</code>
          </div>
          <p className="text-sm text-slate-500">MAGAZAADI yerine kendi mağaza adınızı yazın.</p>
        </div>
      )
    },
    {
      title: "Uygulama Geliştirme Modunu Aç",
      content: (
        <div className="space-y-3">
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> Sol menüden <strong>Settings</strong> (Ayarlar) tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> <strong>Apps and sales channels</strong> seçin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> Sayfanın üstünde <strong>Develop apps</strong> butonuna tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">4.</span> Eğer ilk defa açıyorsanız, <strong>"Allow custom app development"</strong> butonuna tıklayın</li>
          </ol>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            Bu adım sadece bir kere yapılır. Daha önce aktifleştirdiyseniz atlayabilirsiniz.
          </div>
        </div>
      )
    },
    {
      title: "Yeni Uygulama Oluştur",
      content: (
        <div className="space-y-3">
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> <strong>"Create an app"</strong> butonuna tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> Uygulama adı girin (örn: <strong>"Nebim Entegrasyon"</strong>)</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> <strong>"Create app"</strong> ile onaylayın</li>
          </ol>
        </div>
      )
    },
    {
      title: "API İzinlerini (Scopes) Ayarla",
      content: (
        <div className="space-y-3">
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> Uygulama sayfasında <strong>"Configure Admin API scopes"</strong> tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> Aşağıdaki izinleri aktif edin:</li>
          </ol>
          <div className="p-3 bg-slate-50 rounded-lg border text-xs space-y-1">
            <p><strong>Zorunlu izinler:</strong></p>
            <ul className="grid grid-cols-2 gap-1 mt-1">
              <li>read_products / write_products</li>
              <li>read_orders / write_orders</li>
              <li>read_inventory / write_inventory</li>
              <li>read_customers / write_customers</li>
              <li>read_fulfillments / write_fulfillments</li>
              <li>read_locations</li>
            </ul>
          </div>
          <p className="text-xs text-slate-500">Emin değilseniz tüm Admin API izinlerini açabilirsiniz.</p>
          <ol start={3} className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> <strong>"Save"</strong> tıklayın</li>
          </ol>
        </div>
      )
    },
    {
      title: "Uygulamayı Kur ve Token'ı Al",
      content: (
        <div className="space-y-3">
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> <strong>"Install app"</strong> butonuna tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> Onay kutusunda <strong>"Install"</strong> tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> <strong>Admin API access token</strong> gösterilecek</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">4.</span> <strong>"Reveal token once"</strong> tıklayıp token'ı kopyalayın</li>
          </ol>
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            <strong>DİKKAT:</strong> Token sadece BİR KEZ gösterilir! Kopyalayıp güvenli bir yere kaydedin. Token <code>shpat_</code> ile başlar.
          </div>
        </div>
      )
    },
    {
      title: "Sisteme Mağazayı Ekle",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Şimdi bu sayfada <strong>"Mağaza Ekle"</strong> butonuna tıklayıp:</p>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> <strong>"Access Token ile Ekle"</strong> seçin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> Mağaza adını girin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> Mağaza URL'ini girin (örn: <code>magazam.myshopify.com</code>)</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">4.</span> Kopyaladığınız <code>shpat_</code> token'ı yapıştırın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">5.</span> <strong>"Mağazayı Ekle"</strong> tıklayın</li>
          </ol>
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
            <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
            Ekledikten sonra <strong>"Bağlantıyı Test Et"</strong> butonuyla doğrulamayı unutmayın!
          </div>
        </div>
      )
    }
  ];

  const oauthSteps = [
    {
      title: "Shopify Partner Dashboard'a Giriş",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Shopify Partner hesabınıza giriş yapın:</p>
          <div className="p-3 bg-slate-50 rounded-lg border text-sm flex items-center justify-between">
            <code>https://partners.shopify.com</code>
            <CopyButton text="https://partners.shopify.com" id="partner-url" />
          </div>
          <p className="text-xs text-slate-500">Partner hesabınız yoksa Shopify Admin'den de uygulama oluşturabilirsiniz (Settings &rarr; Apps &rarr; Develop apps).</p>
        </div>
      )
    },
    {
      title: "Uygulama Oluştur veya Seç",
      content: (
        <div className="space-y-3">
          <p className="text-sm"><strong>Yeni uygulama için:</strong></p>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> Partner Dashboard'da <strong>"Apps"</strong> menüsüne gidin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> <strong>"Create app"</strong> tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> Uygulama adı girin (örn: <strong>"Nebim Entegrasyon"</strong>)</li>
          </ol>
          <p className="text-sm mt-2"><strong>Mevcut uygulama için:</strong></p>
          <p className="text-sm">Uygulamalar listesinden ilgili uygulamayı seçin.</p>
        </div>
      )
    },
    {
      title: "Uygulama URL'sini Ayarla",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Uygulama ayarlarında <strong>"App setup"</strong> veya <strong>"App URL"</strong> bölümüne gidin:</p>
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-slate-500">App URL (Uygulama URL'si):</Label>
              <div className="p-2.5 bg-slate-50 rounded-lg border text-sm flex items-center justify-between mt-1">
                <code>https://salesofproduct.com/</code>
                <CopyButton text="https://salesofproduct.com/" id="app-url" />
              </div>
            </div>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
            <strong>Bu URL tüm mağazalar için aynıdır.</strong> Her mağaza için ayrı URL girmenize gerek yok.
          </div>
        </div>
      )
    },
    {
      title: "Yönlendirme URL'sini (Redirect URI) Ekle",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Aynı sayfada <strong>"Allowed redirection URL(s)"</strong> veya <strong>"İzin verilen yönlendirme URL'leri"</strong> alanını bulun:</p>
          <div>
            <Label className="text-xs text-slate-500">Redirect URI (Yönlendirme URL'si):</Label>
            <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 text-sm flex items-center justify-between mt-1">
              <code className="text-emerald-800 text-xs break-all">https://salesofproduct.com/api/stores/oauth/callback</code>
              <CopyButton text="https://salesofproduct.com/api/stores/oauth/callback" id="redirect-uri" />
            </div>
          </div>
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
            <strong>Bu adım zorunludur!</strong> Redirect URI eklenmezse OAuth yetkilendirme çalışmaz.
          </div>
          <p className="text-xs text-slate-500">Değişiklikleri kaydetmeyi unutmayın.</p>
        </div>
      )
    },
    {
      title: "API İzinlerini (Scopes) Ayarla",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Uygulama ayarlarında <strong>"API scopes"</strong> veya <strong>"Erişim kapsamları"</strong> bölümüne gidin:</p>
          <div className="p-3 bg-slate-50 rounded-lg border text-xs space-y-1">
            <p><strong>Zorunlu izinler:</strong></p>
            <ul className="grid grid-cols-2 gap-1 mt-1">
              <li>read_products / write_products</li>
              <li>read_orders / write_orders</li>
              <li>read_inventory / write_inventory</li>
              <li>read_customers / write_customers</li>
              <li>read_fulfillments / write_fulfillments</li>
              <li>read_locations</li>
            </ul>
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <strong>Neden bu izinler gerekli?</strong>
            <ul className="mt-1 space-y-0.5">
              <li>- <strong>Siparişler:</strong> Shopify siparişlerini çekip Nebim'e aktarmak için</li>
              <li>- <strong>Ürünler:</strong> Stok ve fiyat güncellemesi için</li>
              <li>- <strong>Müşteriler:</strong> Müşteri bilgilerini Nebim'e senkronize etmek için</li>
              <li>- <strong>Envanter:</strong> Stok seviyelerini yönetmek için</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "API Key ve Secret'ı Kopyala",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Uygulama sayfasında <strong>"Client credentials"</strong> veya <strong>"API credentials"</strong> bölümünü bulun:</p>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> <strong>API Key</strong> (Client ID) - Açıkça gösterilir, kopyalayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> <strong>API Secret Key</strong> (Client Secret) - <code>shpss_</code> ile başlar, kopyalayın</li>
          </ol>
          <div className="p-3 bg-slate-50 rounded-lg border text-xs">
            <p><strong>Örnek:</strong></p>
            <p className="mt-1">API Key: <code>65b1e7e40001230e6f2aa06477f17017</code></p>
            <p>API Secret: <code>shpss_15fa9c701cdde6628427b...</code></p>
          </div>
        </div>
      )
    },
    {
      title: "Mağazaya Uygulamayı Kur",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Uygulamayı bağlamak istediğiniz mağazaya kurmanız gerekiyor:</p>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> Partner Dashboard'da uygulamanızı seçin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> <strong>"Test on development store"</strong> veya <strong>"Select store"</strong> tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> Bağlamak istediğiniz mağazayı seçin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">4.</span> <strong>"Install app"</strong> tıklayın</li>
          </ol>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
            Her yeni mağaza için bu kurulum adımını tekrarlamanız gerekir.
          </div>
        </div>
      )
    },
    {
      title: "Sisteme OAuth ile Bağlan",
      content: (
        <div className="space-y-3">
          <p className="text-sm">Artık sisteme mağazanızı ekleyebilirsiniz:</p>
          <ol className="space-y-2 text-sm">
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">1.</span> <strong>"Mağaza Ekle"</strong> butonuna tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">2.</span> <strong>"OAuth ile Bağlan"</strong> seçin</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">3.</span> Mağaza URL'ini girin (örn: <code>magazam.myshopify.com</code>)</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">4.</span> Kopyaladığınız <strong>API Key</strong> ve <strong>API Secret</strong>'ı yapıştırın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">5.</span> <strong>"Shopify ile Yetkilendir"</strong> tıklayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">6.</span> Shopify sayfasında izinleri onaylayın</li>
            <li className="flex gap-2"><span className="font-bold text-emerald-600 shrink-0">7.</span> Otomatik olarak geri döneceksiniz</li>
          </ol>
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
            <CheckCircle className="w-3.5 h-3.5 inline mr-1" />
            Yetkilendirme bir kez yapılır, token otomatik kaydedilir. Tekrar yapmanıza gerek kalmaz!
          </div>
        </div>
      )
    }
  ];

  const steps = method === "classic" ? classicSteps : oauthSteps;
  const totalSteps = steps.length;
  const current = steps[step];

  return (
    <div className="space-y-4" data-testid="setup-guide">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 px-2 text-xs" data-testid="guide-back-btn">
            <ChevronLeft className="w-3 h-3 mr-1" /> Geri
          </Button>
          <Badge className={method === "classic" ? "bg-slate-100 text-slate-700 hover:bg-slate-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}>
            {method === "classic" ? "Access Token Rehberi" : "OAuth Kurulum Rehberi"}
          </Badge>
        </div>
        <span className="text-xs text-slate-400">{step + 1} / {totalSteps}</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${method === "classic" ? "bg-slate-600" : "bg-emerald-500"}`}
          style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step content */}
      <div className="min-h-[280px]">
        <h3 className="font-semibold text-base mb-3 text-slate-800">
          Adım {step + 1}: {current.title}
        </h3>
        <div className="text-slate-600">{current.content}</div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setStep(s => s - 1)}
          disabled={step === 0}
          data-testid="guide-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Önceki
        </Button>
        {step < totalSteps - 1 ? (
          <Button
            size="sm"
            onClick={() => setStep(s => s + 1)}
            data-testid="guide-next"
          >
            Sonraki <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onClose}
            className={method === "classic" ? "" : "bg-emerald-600 hover:bg-emerald-700"}
            data-testid="guide-done"
          >
            <CheckCircle className="w-4 h-4 mr-1" /> Anladım, Mağaza Ekle
          </Button>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN STORES PAGE ====================
export default function StoresPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [testingStoreId, setTestingStoreId] = useState(null);
  const [transferringStoreId, setTransferringStoreId] = useState(null);
  const [addMethod, setAddMethod] = useState(null); // null | "classic" | "oauth"
  const [showGuide, setShowGuide] = useState(null); // null | "classic" | "oauth"
  const [oauthLoading, setOauthLoading] = useState(false);

  const [newStore, setNewStore] = useState({
    store_name: "",
    store_url: "",
    access_token: "",
    api_key: "",
    api_secret: "",
    location_id: "",
  });

  // Handle OAuth callback
  useEffect(() => {
    const oauthResult = searchParams.get("oauth");
    if (oauthResult === "success") {
      toast.success("Mağaza OAuth ile başarıyla bağlandı!");
      searchParams.delete("oauth");
      setSearchParams(searchParams, { replace: true });
      fetchStores();
    } else if (oauthResult === "error") {
      const detail = searchParams.get("detail") || "Bilinmeyen hata";
      toast.error(`OAuth hatası: ${detail}`);
      searchParams.delete("oauth");
      searchParams.delete("detail");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => { fetchStores(); }, [token]);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores?token=${token}`);
      setStores(response.data.stores);
    } catch (error) {
      toast.error("Mağazalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const addStoreClassic = async () => {
    if (!newStore.store_name || !newStore.store_url || !newStore.access_token) {
      toast.error("Mağaza adı, URL ve Access Token zorunludur");
      return;
    }
    try {
      await axios.post(`${API}/stores?token=${token}`, newStore);
      toast.success("Mağaza eklendi");
      resetDialog();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mağaza eklenemedi");
    }
  };

  const startOAuth = async () => {
    if (!newStore.store_url || !newStore.api_key || !newStore.api_secret) {
      toast.error("Mağaza URL, API Key ve API Secret zorunludur");
      return;
    }
    setOauthLoading(true);
    try {
      const response = await axios.post(`${API}/stores/oauth/start?token=${token}`, {
        store_url: newStore.store_url,
        api_key: newStore.api_key,
        api_secret: newStore.api_secret,
        store_name: newStore.store_name || newStore.store_url.split(".")[0],
        callback_base_url: window.location.origin
      });
      if (response.data.auth_url) {
        toast.info("Shopify yetkilendirme sayfasına yönlendiriliyorsunuz...");
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "OAuth başlatılamadı");
    } finally {
      setOauthLoading(false);
    }
  };

  const resetDialog = () => {
    setAddDialogOpen(false);
    setAddMethod(null);
    setShowGuide(null);
    setNewStore({ store_name: "", store_url: "", access_token: "", api_key: "", api_secret: "", location_id: "" });
  };

  const deleteStore = async (storeId) => {
    if (!window.confirm("Bu mağazayı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/stores/${storeId}?token=${token}`);
      toast.success("Mağaza silindi");
      fetchStores();
    } catch (error) {
      toast.error("Mağaza silinemedi");
    }
  };

  const testConnection = async (storeId) => {
    setTestingStoreId(storeId);
    try {
      const response = await axios.post(`${API}/stores/${storeId}/test-connection?token=${token}`);
      if (response.data.success) {
        toast.success(`Bağlantı başarılı! Mağaza: ${response.data.shop_name}`);
        fetchStores();
      } else {
        toast.error(`Bağlantı hatası: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Bağlantı testi başarısız");
    } finally {
      setTestingStoreId(null);
    }
  };

  const transferSampleProducts = async (storeId, storeName) => {
    if (!window.confirm(`${storeName} mağazasına 5 örnek ürün aktarmak istiyor musunuz?`)) return;
    setTransferringStoreId(storeId);
    try {
      const response = await axios.post(`${API}/stores/${storeId}/create-sample-products?token=${token}`);
      toast.success(`${response.data.success_count} ürün aktarıldı!`);
      if (response.data.error_count > 0) toast.warning(`${response.data.error_count} ürün aktarılamadı`);
    } catch (error) {
      toast.error("Ürünler aktarılamadı");
    } finally {
      setTransferringStoreId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="stores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Shopify Mağazaları</h1>
          <p className="text-slate-500 mt-1">Bağlı Shopify mağazalarınızı yönetin</p>
        </div>
        
        <Dialog open={addDialogOpen} onOpenChange={(open) => { if (!open) resetDialog(); else setAddDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-store">
              <Plus className="w-4 h-4 mr-2" />
              Mağaza Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className={showGuide ? "max-w-lg" : "max-w-md"}>
            <DialogHeader>
              <DialogTitle>{showGuide ? "Kurulum Rehberi" : "Yeni Shopify Mağazası Ekle"}</DialogTitle>
            </DialogHeader>

            {/* Show guide when requested */}
            {showGuide && (
              <SetupGuide
                method={showGuide}
                onClose={() => {
                  setShowGuide(null);
                  setAddMethod(showGuide);
                }}
              />
            )}

            {/* Method selection */}
            {!addMethod && !showGuide && (
              <div className="space-y-3 mt-4">
                <p className="text-sm text-slate-500">Bağlantı yöntemini seçin:</p>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 flex items-start gap-3 justify-start"
                    onClick={() => setAddMethod("classic")}
                    data-testid="btn-method-classic"
                  >
                    <Key className="w-5 h-5 mt-0.5 text-slate-600 shrink-0" />
                    <div className="text-left flex-1">
                      <p className="font-medium">Access Token ile Ekle</p>
                      <p className="text-xs text-slate-500 font-normal mt-1">
                        Mevcut shpat_ token'ınız varsa (2026 öncesi uygulamalar)
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      onClick={(e) => { e.stopPropagation(); setShowGuide("classic"); }}
                      data-testid="btn-help-classic"
                    >
                      <HelpCircle className="w-3.5 h-3.5 mr-1" /> Nasıl yapılır?
                    </Button>
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 flex items-start gap-3 justify-start border-emerald-200 hover:bg-emerald-50"
                    onClick={() => setAddMethod("oauth")}
                    data-testid="btn-method-oauth"
                  >
                    <Link2 className="w-5 h-5 mt-0.5 text-emerald-600 shrink-0" />
                    <div className="text-left flex-1">
                      <p className="font-medium text-emerald-700">OAuth ile Bağlan</p>
                      <p className="text-xs text-slate-500 font-normal mt-1">
                        Shopify 2026+ yeni sistem. API Key ve Secret ile
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      onClick={(e) => { e.stopPropagation(); setShowGuide("oauth"); }}
                      data-testid="btn-help-oauth"
                    >
                      <HelpCircle className="w-3.5 h-3.5 mr-1" /> Nasıl yapılır?
                    </Button>
                  </Button>
                </div>
              </div>
            )}

            {/* Classic method form */}
            {addMethod === "classic" && !showGuide && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setAddMethod(null)} className="h-7 px-2 text-xs">
                      <ChevronLeft className="w-3 h-3 mr-1" /> Geri
                    </Button>
                    <Badge variant="outline">Access Token Yöntemi</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-blue-600"
                    onClick={() => setShowGuide("classic")}
                    data-testid="btn-help-classic-inline"
                  >
                    <HelpCircle className="w-3.5 h-3.5 mr-1" /> Yardım
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Mağaza Adı *</Label>
                  <Input placeholder="Örn: Ana Mağazam" value={newStore.store_name} onChange={(e) => setNewStore({ ...newStore, store_name: e.target.value })} data-testid="input-new-store-name" />
                </div>
                <div className="space-y-2">
                  <Label>Mağaza URL *</Label>
                  <Input placeholder="magazam.myshopify.com" value={newStore.store_url} onChange={(e) => setNewStore({ ...newStore, store_url: e.target.value })} data-testid="input-new-store-url" />
                </div>
                <div className="space-y-2">
                  <Label>Admin API Access Token *</Label>
                  <Input type="password" placeholder="shpat_xxxxx" value={newStore.access_token} onChange={(e) => setNewStore({ ...newStore, access_token: e.target.value })} data-testid="input-new-access-token" />
                </div>
                <div className="space-y-2">
                  <Label>API Key (Opsiyonel)</Label>
                  <Input placeholder="API Key" value={newStore.api_key} onChange={(e) => setNewStore({ ...newStore, api_key: e.target.value })} data-testid="input-new-api-key" />
                </div>
                <div className="space-y-2">
                  <Label>API Secret (Opsiyonel)</Label>
                  <Input type="password" placeholder="API Secret" value={newStore.api_secret} onChange={(e) => setNewStore({ ...newStore, api_secret: e.target.value })} data-testid="input-new-api-secret" />
                </div>
                <div className="space-y-2">
                  <Label>Location ID (Opsiyonel)</Label>
                  <Input placeholder="Boş bırakılırsa tüm location'lara stok gönderilir" value={newStore.location_id} onChange={(e) => setNewStore({ ...newStore, location_id: e.target.value })} data-testid="input-new-location-id" />
                  <p className="text-xs text-muted-foreground">Shopify Admin → Settings → Locations'dan bulabilirsiniz</p>
                </div>
                <Button onClick={addStoreClassic} className="w-full" data-testid="btn-save-store">
                  Mağazayı Ekle
                </Button>
              </div>
            )}

            {/* OAuth method form */}
            {addMethod === "oauth" && !showGuide && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setAddMethod(null)} className="h-7 px-2 text-xs">
                      <ChevronLeft className="w-3 h-3 mr-1" /> Geri
                    </Button>
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OAuth Yöntemi</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-blue-600"
                    onClick={() => setShowGuide("oauth")}
                    data-testid="btn-help-oauth-inline"
                  >
                    <HelpCircle className="w-3.5 h-3.5 mr-1" /> Yardım
                  </Button>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  Shopify 2026 sonrası uygulamalar OAuth yetkilendirme gerektirir.
                  API Key ve API Secret girdikten sonra Shopify'a yönlendirileceksiniz.
                </div>
                <div className="space-y-2">
                  <Label>Mağaza Adı</Label>
                  <Input placeholder="Örn: Yeni Mağazam" value={newStore.store_name} onChange={(e) => setNewStore({ ...newStore, store_name: e.target.value })} data-testid="input-oauth-store-name" />
                </div>
                <div className="space-y-2">
                  <Label>Mağaza URL *</Label>
                  <Input placeholder="magazam.myshopify.com" value={newStore.store_url} onChange={(e) => setNewStore({ ...newStore, store_url: e.target.value })} data-testid="input-oauth-store-url" />
                </div>
                <div className="space-y-2">
                  <Label>API Key (Client ID) *</Label>
                  <Input placeholder="65b1e7e400012..." value={newStore.api_key} onChange={(e) => setNewStore({ ...newStore, api_key: e.target.value })} data-testid="input-oauth-api-key" />
                </div>
                <div className="space-y-2">
                  <Label>API Secret (Client Secret) *</Label>
                  <Input type="password" placeholder="shpss_xxxxx" value={newStore.api_secret} onChange={(e) => setNewStore({ ...newStore, api_secret: e.target.value })} data-testid="input-oauth-api-secret" />
                </div>
                <Button onClick={startOAuth} className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={oauthLoading} data-testid="btn-start-oauth">
                  {oauthLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Shopify ile Yetkilendir
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stores Grid */}
      {stores.length === 0 ? (
        <Card className="p-12 text-center">
          <Store className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Henüz mağaza eklenmemiş</h3>
          <p className="text-slate-500 mb-4">Shopify mağazanızı ekleyerek ürün aktarımına başlayın</p>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> İlk Mağazanızı Ekleyin
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <Card key={store.id} className="card-hover" data-testid={`store-card-${store.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Store className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{store.store_name}</CardTitle>
                      <StoreNameEditor store={store} token={token} onUpdate={fetchStores} />
                      <CardDescription className="text-xs">{store.store_url}</CardDescription>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={store.connection_tested ? "badge-success" : "badge-warning"}>
                      {store.connection_tested ? "Bağlı" : "Test Edilmedi"}
                    </Badge>
                    {store.oauth_connected && (
                      <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-600">OAuth</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {store.shop_name && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p><span className="text-slate-500">Mağaza:</span> {store.shop_name}</p>
                    {store.currency && <p><span className="text-slate-500">Para Birimi:</span> {store.currency}</p>}
                    {store.plan_name && <p><span className="text-slate-500">Plan:</span> {store.plan_name}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500 text-sm">Location:</span>
                      <LocationSelector storeId={store.id} currentLocationId={store.location_id} token={token} onUpdate={fetchStores} />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-slate-500 text-sm">Para Birimi:</span>
                      <select
                        value={store.currency || "TRY"}
                        onChange={async (e) => {
                          try {
                            await axios.post(`${API}/stores/${store.id}/update?token=${token}`, { currency: e.target.value });
                            toast.success(`Para birimi: ${e.target.value}`);
                            fetchStores();
                          } catch (err) {
                            toast.error('Para birimi guncellenemedi');
                          }
                        }}
                        className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white focus:ring-blue-500 focus:border-blue-500"
                        data-testid={`select-currency-${store.id}`}
                      >
                        <option value="TRY">TRY (₺)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-slate-500 text-sm">Diller:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { code: "TR", label: "Turkce" },
                          { code: "EN", label: "English" },
                          { code: "AR", label: "Arabic" },
                          { code: "RU", label: "Russian" },
                          { code: "DE", label: "Deutsch" },
                        ].map((lang) => {
                          const langs = store.languages || ["TR"];
                          const isSelected = langs.includes(lang.code);
                          return (
                            <button
                              key={lang.code}
                              onClick={async () => {
                                const newLangs = isSelected
                                  ? langs.filter((l) => l !== lang.code)
                                  : [...langs, lang.code];
                                if (newLangs.length === 0) return;
                                try {
                                  await axios.post(`${API}/stores/${store.id}/update?token=${token}`, { languages: newLangs });
                                  toast.success(`Diller: ${newLangs.join(", ")}`);
                                  fetchStores();
                                } catch (err) {
                                  toast.error("Dil guncellenemedi");
                                }
                              }}
                              className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                                isSelected
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                              }`}
                              data-testid={`lang-${lang.code}-${store.id}`}
                            >
                              {lang.code}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        id={`sync-${store.id}`}
                        checked={store.stock_price_sync !== false}
                        onChange={async (e) => {
                          try {
                            await axios.post(`${API}/stores/${store.id}/update?token=${token}`, { stock_price_sync: e.target.checked });
                            toast.success(e.target.checked ? 'Stok/Fiyat sync aktif' : 'Stok/Fiyat sync kapatıldı');
                            fetchStores();
                          } catch (err) {
                            toast.error('Güncelleme başarısız');
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`checkbox-sync-${store.id}`}
                      />
                      <label htmlFor={`sync-${store.id}`} className="text-sm text-slate-600 cursor-pointer">
                        Stok/Fiyat Güncelle
                      </label>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Button variant="outline" size="sm" onClick={() => testConnection(store.id)} disabled={testingStoreId === store.id} data-testid={`btn-test-${store.id}`}>
                    {testingStoreId === store.id ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Bağlantıyı Test Et
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => transferSampleProducts(store.id, store.store_name)} disabled={transferringStoreId === store.id || !store.connection_tested} data-testid={`btn-transfer-${store.id}`}>
                    {transferringStoreId === store.id ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    5 Örnek Ürün Aktar
                  </Button>
                  {store.shop_domain && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`https://${store.shop_domain}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-2" /> Mağazayı Aç
                      </a>
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteStore(store.id)} data-testid={`btn-delete-${store.id}`}>
                    <Trash2 className="w-4 h-4 mr-2" /> Mağazayı Sil
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
